import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { canadianCities, getAllProvinces, getCitiesByProvince } from '@/data/cities';
import { Badge } from '@/components/primitives/Badge';
import { Card } from '@/components/ui/card';
import { MapPin, Snowflake, Users, ArrowRight } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { SEO, SEO_CONFIG } from '@/components/primitives/SEO';

export function CitiesDirectoryPage() {
  const provinces = getAllProvinces();

  const handleCityClick = (cityName: string) => {
    trackEvent('city_directory_click', { city: cityName });
  };

  return (
    <>
      <SEO {...SEO_CONFIG.cities} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-brand-900 via-brand-800 to-brand-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff0f_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0f_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative container-wide mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm border border-accent-500/30 rounded-full mb-6"
            >
              <MapPin className="w-4 h-4 text-accent-500" />
              <span className="text-sm font-bold text-accent-500 uppercase tracking-wider">
                Canada-Wide Coverage
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              SNOWBLOWR in<br />
              <span className="gradient-text">Your City</span>
            </motion.h1>

            <motion.p
              className="text-xl text-snow-100 mb-8 font-semibold"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              On-demand snow removal across {canadianCities.length}+ major Canadian cities.<br />
              Find your city and get started today.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Cities by Province */}
      <section className="py-16 md:py-24 bg-neutral-50">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          {provinces.map((province, provinceIndex) => {
            const provinceCities = getCitiesByProvince(
              canadianCities.find(c => c.province === province)?.provinceCode || ''
            );

            return (
              <motion.div
                key={province}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: provinceIndex * 0.1 }}
                className="mb-16 last:mb-0"
              >
                <div className="mb-8">
                  <Badge variant="brand" className="mb-3">
                    {provinceCities[0]?.provinceCode}
                  </Badge>
                  <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-2">
                    {province}
                  </h2>
                  <p className="text-lg text-neutral-600 font-medium">
                    {provinceCities.length} {provinceCities.length === 1 ? 'city' : 'cities'} covered
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {provinceCities.map((city, cityIndex) => (
                    <motion.div
                      key={city.slug}
                      initial={{ opacity: 0, scale: 0.95 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: cityIndex * 0.05 }}
                    >
                      <Link
                        to={`/cities/${city.slug}`}
                        onClick={() => handleCityClick(city.name)}
                        className="block h-full"
                      >
                        <Card 
                          variant="solid" 
                          className="h-full p-6 hover:shadow-xl hover:border-brand-500 transition-all duration-300 group cursor-pointer"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="text-2xl font-black text-neutral-900 mb-1 group-hover:text-brand-500 transition-colors">
                                {city.name}
                              </h3>
                              <p className="text-sm text-neutral-600 font-semibold">
                                {city.marketSize}
                              </p>
                            </div>
                            <ArrowRight className="w-6 h-6 text-brand-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                          </div>

                          <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-2">
                              <Snowflake className="w-5 h-5 text-brand-500" />
                              <span className="text-sm font-medium text-neutral-700">
                                {city.avgSnowfall}cm avg snowfall
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Users className="w-5 h-5 text-brand-500" />
                              <span className="text-sm font-medium text-neutral-700">
                                {city.population.toLocaleString()} population
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-brand-500" />
                              <span className="text-sm font-medium text-neutral-700">
                                {city.keyNeighborhoods.length} neighborhoods
                              </span>
                            </div>
                          </div>

                          <div className="pt-4 border-t border-neutral-200">
                            <p className="text-xs text-neutral-600 font-medium line-clamp-2">
                              {city.seasonalNotes}
                            </p>
                          </div>
                        </Card>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-white">
        <div className="container-wide mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-black mb-6">On-Demand Snow Removal Across Canada</h2>
            <p className="text-neutral-700 font-medium leading-relaxed mb-6">
              SNOWBLOWR is Canada's leading on-demand snow removal platform, connecting homeowners with verified local 
              operators across major cities from coast to coast. Whether you're in Toronto dealing with lake effect snow, 
              Ottawa facing record snowfall, or Vancouver preparing for rare winter storms, SNOWBLOWR has you covered.
            </p>
            <p className="text-neutral-700 font-medium leading-relaxed mb-6">
              Our city-specific approach means each market is tailored to local conditions, pricing reflects regional standards, 
              and our operator network understands the unique challenges of your area. From the extreme snowfall of St. John's 
              to the chinook winds of Calgary, we've built SNOWBLOWR to handle Canada's diverse winter conditions.
            </p>
            <h3 className="text-2xl font-black mb-4">Why Choose SNOWBLOWR?</h3>
            <ul className="space-y-2 mb-6 list-none pl-0">
              <li className="text-neutral-700 font-medium">✓ Available in {canadianCities.length}+ major Canadian cities</li>
              <li className="text-neutral-700 font-medium">✓ Verified, insured operators in every market</li>
              <li className="text-neutral-700 font-medium">✓ City-specific pricing and service standards</li>
              <li className="text-neutral-700 font-medium">✓ Real-time tracking and photo verification</li>
              <li className="text-neutral-700 font-medium">✓ No contracts, transparent pricing</li>
              <li className="text-neutral-700 font-medium">✓ Bilingual service in Quebec markets</li>
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}

